import { MdClose } from 'react-icons/md';
import { Portal } from '../../../components/Modal';
import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { Button, SelectDropDown } from '@wot/components';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import adminPaymentsApi, { IAdminPayment } from '../../../api/adminPayments';
import Chip from '../../../components/Chip';

const STATUS_OPTIONS = ['success', 'pending', 'failed'];

export default function RegistrationDetail() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [payment, setPayment] = useState<IAdminPayment | null>(null);
  const [status, setStatus] = useState('success');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const { paymentId } = useParams();

  useEffect(() => {
    if (paymentId) {
      adminPaymentsApi.getById(paymentId).then((data) => {
        setPayment(data);
        setStatus(data.status || 'success');
      });
    }
  }, [paymentId]);

  const handleClose = () => navigate(-1);

  const handleSave = async () => {
    if (!payment) return;
    setSaving(true);
    try {
      await adminPaymentsApi.update(payment._id, { status });
      toast.success('Registration updated');
      queryClient.invalidateQueries({ queryKey: ['summer-camp-registrations'] });
      handleClose();
    } catch {
      toast.error('Failed to update registration');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!payment) return;
    setDeleting(true);
    try {
      await adminPaymentsApi.remove(payment._id);
      toast.success('Registration deleted');
      queryClient.invalidateQueries({ queryKey: ['summer-camp-registrations'] });
      handleClose();
    } catch {
      toast.error('Failed to delete registration');
    } finally {
      setDeleting(false);
    }
  };

  if (!payment) return null;

  const ws = payment.workshop;
  const student = payment.studentData;

  return (
    <Portal>
      <div className="relative w-full h-screen flex justify-center -z-10">
        <div onClick={handleClose} className="absolute top-0 left-0 w-full h-full bg-black/70 -z-10" />
        <div className="w-full h-full mx-auto my-auto flex justify-end">
          <div className="w-[40%] h-full flex flex-col gap-5 bg-white rounded-l-2xl p-7 overflow-y-scroll">
            <div className="w-full flex items-center justify-between">
              <h1 className="text-lg font-semibold">Registration {payment._id.slice(-8)}</h1>
              <MdClose className="text-2xl cursor-pointer" onClick={handleClose} />
            </div>

            <div className="flex flex-col gap-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Date</span>
                <span className="font-medium">{format(parseISO(payment.createdAt), 'd LLL, yyyy h:mm a')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Amount</span>
                <span className="font-medium">{payment.currency} {payment.amount}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Payment Method</span>
                <span className="font-medium capitalize">{payment.paymentMethod || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Email Sent</span>
                <Chip state={payment.emailSent ? 'success' : 'error'}>
                  <p className="text-sm font-semibold">{payment.emailSent ? 'Yes' : 'No'}</p>
                </Chip>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Admin Notified</span>
                <Chip state={payment.adminNotified ? 'success' : 'error'}>
                  <p className="text-sm font-semibold">{payment.adminNotified ? 'Yes' : 'No'}</p>
                </Chip>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-500">Status</span>
                <div className="w-40">
                  <SelectDropDown
                    value={status}
                    onChange={(val) => setStatus(val as string)}
                    options={STATUS_OPTIONS}
                    placeholder="Select status"
                    size="small"
                  />
                </div>
              </div>
            </div>

            <div className="border-t pt-4">
              <h2 className="text-base font-semibold mb-3">Student Details</h2>
              <div className="flex flex-col gap-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Name</span>
                  <span className="font-medium">{student?.name || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Email</span>
                  <span className="font-medium">{student?.email || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Phone</span>
                  <span className="font-medium">{student?.phone || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Age</span>
                  <span className="font-medium">{student?.age ?? '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Experience</span>
                  <span className="font-medium">{student?.experience || '-'}</span>
                </div>
              </div>
            </div>

            <div className="border-t pt-4">
              <h2 className="text-base font-semibold mb-3">Workshop Details</h2>
              <div className="flex flex-col gap-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Title</span>
                  <span className="font-medium">{ws?.title || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Category</span>
                  <span className="font-medium">{ws?.category || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Hours</span>
                  <span className="font-medium">{ws?.hours || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Price</span>
                  <span className="font-medium">{ws?.price || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Offer Price</span>
                  <span className="font-medium">{ws?.offerPrice ?? '-'}</span>
                </div>
              </div>
            </div>

            <div className="border-t pt-4">
              <h2 className="text-base font-semibold mb-3">Payment Details</h2>
              <div className="flex flex-col gap-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Razorpay Order ID</span>
                  <span className="font-medium text-xs break-all max-w-[200px] text-right">{payment.razorpay_order_id || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Razorpay Payment ID</span>
                  <span className="font-medium text-xs break-all max-w-[200px] text-right">{payment.razorpay_payment_id || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Razorpay Signature</span>
                  <span className="font-medium text-xs break-all max-w-[200px] text-right">{payment.razorpay_signature || '-'}</span>
                </div>
              </div>
            </div>

            <div className="w-full flex items-center gap-5 mt-auto">
              <Button type="s-outlined" block text="Cancel" onClick={handleClose} />
              <Button type="primary" block text="Delete" loading={deleting} onClick={handleDelete} className="!bg-red-600 !border-red-600 hover:!bg-red-700" />
              <Button block text="Save" loading={saving} onClick={handleSave} />
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
}
